import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';
// frontend/src/components/tutorial/TutorialOverlay.jsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Portal, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTutorialContext } from '../../contexts/TutorialContext';
import TutorialTooltip from './TutorialTooltip';

const PADDING = 12;
const MAX_FIND_ATTEMPTS = 80;
const FIND_INTERVAL = 150;
const MODAL_STEPS = new Set([i18n.t('hardcoded.components_ai_aientitycards.vehicle'), 'receiver', i18n.t('hardcoded.contexts_tutorialcontext.sensors'), 'assignments', 'alarm_profile']);

const isVisibleElement = (el) => {
  if (!el || !document.contains(el)) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

const getRectWithPadding = (el, padding = PADDING) => {
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
    rawTop: rect.top,
    rawLeft: rect.left,
    rawWidth: rect.width,
    rawHeight: rect.height,
  };
};

const getActiveModalPaper = () => {
  const candidates = Array.from(
    document.querySelectorAll(
      '.MuiDialog-root[aria-hidden="false"], .MuiModal-root[aria-hidden="false"], [role="dialog"][aria-modal="true"]'
    )
  );

  for (let i = candidates.length - 1; i >= 0; i -= 1) {
    const root = candidates[i];
    const paper = root.querySelector('.MuiDialog-paper, .MuiPaper-root') || root;
    if (isVisibleElement(paper)) {
      return paper;
    }
  }
  return null;
};

const TutorialOverlay = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isActive, currentStep } = useTutorialContext();
  const [targetRect, setTargetRect] = useState(null);
  const [secondaryRects, setSecondaryRects] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const findAttemptRef = useRef(0);
  const targetElementRef = useRef(null);
  const interactionRootsRef = useRef([]);
  const tooltipRootRef = useRef(null);
  const secondaryElementsRef = useRef([]);
  const rafRef = useRef(null);
  const findTimerRef = useRef(null);

  const updateRect = useCallback(() => {
    const el = targetElementRef.current;
    if (!el || !document.contains(el)) {
      setTargetRect(null);
      setSecondaryRects([]);
      return;
    }
    try {
      if (!isVisibleElement(el)) return;
      setTargetRect(getRectWithPadding(el));

      const highlighted = secondaryElementsRef.current
        .filter((node) => node && node !== el && isVisibleElement(node))
        .slice(0, 6)
        .map((node) => getRectWithPadding(node, 6));
      setSecondaryRects(highlighted);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!isActive || !currentStep) {
      setTargetRect(null);
      setSecondaryRects([]);
      setIsReady(false);
      targetElementRef.current = null;
      secondaryElementsRef.current = [];
      interactionRootsRef.current = [];
      return;
    }

    if (!currentStep.targetSelector) {
      targetElementRef.current = null;
      setTargetRect(null);
      setSecondaryRects([]);
      setIsReady(true);
      return;
    }

    findAttemptRef.current = 0;
    targetElementRef.current = null;
    secondaryElementsRef.current = [];
    setTargetRect(null);
    setSecondaryRects([]);
    setIsReady(false);

    const tryFind = () => {
      const selector = currentStep.targetSelector;
      const secondarySelectors = currentStep.secondaryTargetSelectors || [];
      try {
        const primaryTarget = document.querySelector(selector);
        const modalTarget = getActiveModalPaper();
        const secondaryTargets = secondarySelectors
          .map((s) => {
            try {
              return document.querySelector(s);
            } catch {
              return null;
            }
          })
          .filter((node) => isVisibleElement(node));

        secondaryElementsRef.current = secondaryTargets;

        let resolvedTarget = null;
        if (modalTarget && MODAL_STEPS.has(currentStep.id)) {
          resolvedTarget = modalTarget;
        } else if (secondaryTargets.length > 0) {
          resolvedTarget = secondaryTargets[0];
        } else if (primaryTarget && isVisibleElement(primaryTarget)) {
          resolvedTarget = primaryTarget;
        }

        const roots = [resolvedTarget, modalTarget, primaryTarget, ...secondaryTargets, tooltipRootRef.current]
          .filter((node) => node && document.contains(node));
        interactionRootsRef.current = roots;

        if (resolvedTarget) {
          targetElementRef.current = resolvedTarget;
          updateRect();
          setIsReady(true);
          try {
            resolvedTarget.scrollIntoView({ behavior: 'smooth', block: 'center', inline: t('hardcoded.pages_chatpage.nearest') });
          } catch { /* ignore */ }
          return;
        }
      } catch { /* ignore */ }

      findAttemptRef.current++;
      if (findAttemptRef.current < MAX_FIND_ATTEMPTS) {
        findTimerRef.current = setTimeout(tryFind, FIND_INTERVAL);
      } else {
        setIsReady(true);
      }
    };

    // Small delay to let route render
    findTimerRef.current = setTimeout(tryFind, 300);

    return () => {
      if (findTimerRef.current) clearTimeout(findTimerRef.current);
    };
  }, [isActive, currentStep, updateRect]);

  // Continuous position tracking
  useEffect(() => {
    if (!isActive || !isReady) return;

    const observer = new MutationObserver(() => {
      if (targetElementRef.current && document.contains(targetElementRef.current)) {
        updateRect();
      } else {
        const selector = currentStep?.targetSelector;
        if (selector) {
          try {
            const el = document.querySelector(selector);
            if (el && isVisibleElement(el)) {
              targetElementRef.current = el;
              updateRect();
            }
          } catch {
            // ignore
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    const handleUpdate = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateRect);
    };
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isActive, isReady, currentStep, updateRect]);

  useEffect(() => {
    if (!isActive || !isReady) return undefined;

    const isAllowedPortalTarget = (target) => {
      if (!(target instanceof Element)) return false;
      return Boolean(
        target.closest('.MuiAutocomplete-popper') ||
        target.closest('.MuiPopper-root') ||
        target.closest('.MuiPopover-root') ||
        target.closest('.MuiMenu-root') ||
        target.closest(t('hardcoded.components_tutorial_tutorialoverlay.rolelistbox')) ||
        target.closest(t('hardcoded.components_tutorial_tutorialoverlay.data-tutorial-tooltiptrue'))
      );
    };

    const isAllowedTarget = (target) => {
      if (!(target instanceof Element)) return false;
      if (isAllowedPortalTarget(target)) return true;

      return interactionRootsRef.current.some((root) => {
        if (!root || !document.contains(root)) return false;
        return root === target || root.contains(target);
      });
    };

    const blockEvent = (event) => {
      if (isAllowedTarget(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.stopImmediatePropagation) {
        event.stopImmediatePropagation();
      }
    };

    document.addEventListener('pointerdown', blockEvent, true);
    document.addEventListener('mousedown', blockEvent, true);
    document.addEventListener('click', blockEvent, true);
    document.addEventListener('touchstart', blockEvent, true);

    return () => {
      document.removeEventListener('pointerdown', blockEvent, true);
      document.removeEventListener('mousedown', blockEvent, true);
      document.removeEventListener('click', blockEvent, true);
      document.removeEventListener('touchstart', blockEvent, true);
    };
  }, [isActive, isReady]);

  if (!isActive || !isReady) return null;

  // Build overlay with clickthrough hole for the target
  const hasTarget = currentStep && targetRect;

  return (
    <Portal>
      <AnimatePresence mode="wait">
        {isActive && (
          <motion.div
            key={currentStep?.id || 'overlay'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998,
              pointerEvents: 'none',
            }}
          >
            {/* Dark overlay with cutout — allows clicks through the hole */}
            {hasTarget && (
              <>
                {/* Top strip */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: Math.max(0, targetRect.top),
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    pointerEvents: 'none',
                  }}
                />
                {/* Bottom strip */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: targetRect.top + targetRect.height,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    pointerEvents: 'none',
                  }}
                />
                {/* Left strip */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: targetRect.top,
                    left: 0,
                    width: Math.max(0, targetRect.left),
                    height: targetRect.height,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    pointerEvents: 'none',
                  }}
                />
                {/* Right strip */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: targetRect.top,
                    left: targetRect.left + targetRect.width,
                    right: 0,
                    height: targetRect.height,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    pointerEvents: 'none',
                  }}
                />

                {/* Glowing border around target */}
                <Box
                  component={motion.div}
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  sx={{
                    position: 'absolute',
                    top: targetRect.top,
                    left: targetRect.left,
                    width: targetRect.width,
                    height: targetRect.height,
                    border: '3px solid',
                    borderColor: theme.palette.error.main,
                    borderRadius: '12px',
                    pointerEvents: 'none',
                    animation: 'tutorial-glow 1.5s ease-in-out infinite',
                    '@keyframes tutorial-glow': {
                      '0%, 100%': {
                        boxShadow: `0 0 0 4px rgba(211, 47, 47, 0.3), 0 0 20px rgba(211, 47, 47, 0.4)`,
                      },
                      '50%': {
                        boxShadow: `0 0 0 8px rgba(211, 47, 47, 0.5), 0 0 40px rgba(211, 47, 47, 0.6)`,
                      },
                    },
                  }}
                />

                {secondaryRects.map((rect, idx) => (
                  <Box
                    key={`secondary-highlight-${idx}`}
                    sx={{
                      position: 'absolute',
                      top: rect.top,
                      left: rect.left,
                      width: rect.width,
                      height: rect.height,
                      border: '2px dashed',
                      borderColor: theme.palette.warning.main,
                      borderRadius: '10px',
                      pointerEvents: 'none',
                      boxShadow: `0 0 0 2px rgba(255, 152, 0, 0.22)`,
                    }}
                  />
                ))}
              </>
            )}

            {/* Full dark overlay when no target */}
            {!hasTarget && currentStep && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  pointerEvents: 'none',
                }}
              />
            )}

            {/* Tooltip panel — always interactive */}
            <Box ref={tooltipRootRef} data-tutorial-tooltip="true" sx={{ pointerEvents: 'auto' }}>
              <TutorialTooltip targetRect={targetRect} />
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  );
};

export default TutorialOverlay;
